/*
 * poaime-hero-3d — Hero immersif WebGL (sans dépendance, raw WebGL1).
 * Simulation plein écran « macro d'huile » : médium liquide vert forêt qui s'écoule,
 * grosses gouttes réfractives (Voronoï arrondi : lentille + bord lumineux + reflet) et
 * vraies CAUSTIQUES (nervures de lumière) qui jouent dans les gouttes.
 * 100 % fragment shader : pas de particules, pas de three.js → léger et rapide (Lighthouse).
 * Best practices : pause hors-écran (IntersectionObserver), respecte prefers-reduced-motion
 * (fallback dégradé CSS), résolution interne plafonnée (mobile vs desktop), fallback si WebGL absent.
 * Couche purement décorative (aria-hidden) — le contenu reste en HTML/réglages.
 */

const VERT = `
  attribute vec2 aPos;
  varying vec2 vUv;
  void main(){
    vUv = aPos * 0.5 + 0.5;
    gl_Position = vec4(aPos, 0.0, 1.0);
  }
`;

const FRAG = `
  #ifdef GL_FRAGMENT_PRECISION_HIGH
  precision highp float;
  #else
  precision mediump float;
  #endif

  varying vec2 vUv;
  uniform float uTime;
  uniform vec2  uRes;
  uniform float uSpeed;
  uniform float uScale;   // densité des gouttes (cellules sur la hauteur)
  uniform vec3  uDeep;    // vert profond (fond)
  uniform vec3  uMid;     // vert médian
  uniform vec3  uAmber;   // ambre (accent)
  uniform vec3  uCream;   // crème (lumière / caustiques / reflets)

  float hash1(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
  vec2  hash2(vec2 p){
    p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
    return fract(sin(p) * 43758.5453123);
  }
  float vnoise(vec2 p){
    vec2 i = floor(p), f = fract(p);
    float a = hash1(i), b = hash1(i + vec2(1.0, 0.0));
    float c = hash1(i + vec2(0.0, 1.0)), d = hash1(i + vec2(1.0, 1.0));
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  }
  float fbm(vec2 p){
    float v = 0.0, a = 0.5;
    for (int i = 0; i < 5; i++){ v += a * vnoise(p); p = p * 2.02 + 11.0; a *= 0.5; }
    return v;
  }

  // Champ de MÉTABALLES : des blobs de matière qui fusionnent et se séparent en coulant
  // (liquide vivant = « organique »). Sort : densité T, direction moyenne vers la masse (toC),
  // mise au point moyenne (foc → profondeur de champ « macro vidéo ») et teinte (tn).
  void field(vec2 x, float t, out float T, out vec2 toC, out float foc, out float tn){
    vec2 n = floor(x), f = fract(x);
    T = 0.0; vec2 cAcc = vec2(0.0); float fAcc = 0.0, tAcc = 0.0, wsum = 0.0;
    for (int j = -1; j <= 1; j++)
    for (int i = -1; i <= 1; i++){
      vec2 g    = vec2(float(i), float(j));
      vec2 o    = hash2(n + g);
      float o2  = hash1(n + g + 3.1);
      float rad = mix(0.42, 0.80, o.x);                  // plus gros -> les blobs fusionnent davantage
      // placement plus aléatoire (casse la régularité de la grille) + orbite lente + respiration
      vec2 jit  = 0.5 + vec2(0.46 * sin(t * 0.9 + 6.2831853 * o.x),
                             0.46 * cos(t * 0.8 + 6.2831853 * o.y));
      float rr  = rad * (0.92 + 0.08 * sin(t * 1.3 + 6.2831853 * o2));
      vec2 r    = g + jit - f;
      float s   = clamp(1.0 - dot(r, r) / (rr * rr), 0.0, 1.0);
      float w   = s * s;                                 // noyau lisse -> fusion douce des blobs
      T    += w;
      cAcc += r * w;
      fAcc += o2 * w;
      tAcc += o.y * w;
      wsum += w;
    }
    float inv = wsum > 0.0001 ? 1.0 / wsum : 0.0;
    toC = cAcc * inv;
    foc = wsum > 0.0001 ? fAcc * inv : 0.5;
    tn  = wsum > 0.0001 ? tAcc * inv : 0.5;
  }

  // Caustiques organiques (technique classique par interférence) — les nervures de lumière.
  float caustic(vec2 uv, float t){
    vec2 p = mod(uv * 6.2831853, 6.2831853) - 250.0;
    vec2 i = p; float c = 1.0; float inten = 0.0045;
    for (int n = 0; n < 5; n++){
      float tt = t * 0.5 * (1.0 - (3.5 / float(n + 1)));
      i = p + vec2(cos(tt - i.x) + sin(tt + i.y), sin(tt - i.y) + cos(tt + i.x));
      c += 1.0 / length(vec2(p.x / (sin(i.x + tt) / inten), p.y / (cos(i.y + tt) / inten)));
    }
    c /= 5.0; c = 1.17 - pow(c, 1.4);
    return clamp(pow(abs(c), 6.0), 0.0, 1.0);
  }

  // Le médium liquide : dégradé vert forêt qui s'écoule, veiné d'ambre.
  vec3 medium(vec2 uv, float t){
    vec2 q = uv;
    q += 0.16 * vec2(fbm(uv * 1.4 + vec2(0.0, t * 0.10)), fbm(uv * 1.4 + vec2(5.2, -t * 0.12)));
    float n = fbm(q * 1.25);
    vec3 col = mix(uDeep, uMid, smoothstep(-0.15, 0.95, n));
    float a = fbm(q * 2.0 + 7.0);
    col = mix(col, mix(uMid, uAmber, 0.6), smoothstep(0.52, 0.98, a) * 0.20);
    return col;
  }

  void main(){
    float aspect = uRes.x / max(uRes.y, 1.0);
    vec2 uv = vUv;
    float t = uTime * 0.11 * uSpeed;          // plus lent = plus chic

    // Repère « monde » (corrigé de l'aspect) + courant global très lent.
    vec2 p  = (uv - 0.5) * vec2(aspect, 1.0);
    vec2 wp = p * uScale;
    wp += 0.16 * vec2(fbm(p * 0.7 + t * 0.10), fbm(p * 0.7 + 4.0 - t * 0.10));

    float T, foc, tn; vec2 toC;
    field(wp, t, T, toC, foc, tn);

    float thr   = 0.13;                       // seuil bas -> les blobs se rejoignent (matière continue)
    float soft  = mix(0.24, 0.06, foc);       // fort flou sur les blobs du fond (profondeur de champ)
    float mask  = smoothstep(thr - soft, thr + soft, T);
    float depth = smoothstep(thr, thr + 0.45, T);   // 0 au bord -> 1 au cœur (volume du blob)

    // OMBRE DOUCE sous les gouttes (drop-shadow façon macro) -> relief avant tout dessin.
    // Lue dans le champ T décalé vers le haut : si une goutte est "au-dessus", on assombrit le fond.
    float Ts, _foc, _tn; vec2 _toCs;
    field(wp - vec2(0.08, 0.30), t, Ts, _toCs, _foc, _tn);
    float shadow = smoothstep(thr - 0.04, thr + 0.22, Ts) * (1.0 - mask);

    // TRANSPARENCE / réfraction : on voit le médium dévié À TRAVERS l'huile (lentille).
    vec2 refr = uv + toC * mix(0.12, 0.24, foc) * mask;
    vec3 col  = medium(refr, t);
    col *= 1.0 - shadow * 0.18;                  // l'ombre se pose sur le médium

    // CORPS de l'huile dorée : champagne au cœur, ambre vers le bord (mode beauté liquide).
    vec3 oilCore = mix(uCream, uAmber, 0.30 + 0.20 * tn);
    vec3 oilEdge = mix(uAmber, uCream, 0.20);
    vec3 oil = mix(oilEdge, oilCore, depth);
    col = mix(col, col * 0.60 + oil * 0.55, mask * 0.55);

    // CAUSTIQUES dorées, discrètes (chic), focalisées dans l'huile.
    float ca = caustic(refr * 2.0 + toC * 0.7, uTime * 0.32 * uSpeed);
    col += mix(uCream, uAmber, 0.60) * ca * (0.04 + 0.14 * mask * foc);

    // BOMBEMENT (gradient haut-clair / bas-foncé sur la goutte) -> volume 3D, plus "plat".
    float dome = smoothstep(0.10, -0.10, toC.y) * mask;
    col = mix(col * 0.92, col * 1.05, dome);

    // Liseré doré, brisé par un bruit (lumière, pas contour), très subtil.
    float rim = smoothstep(thr - 0.02, thr + 0.05, T) * (1.0 - smoothstep(thr + 0.05, thr + 0.20, T));
    float rimBreak = 0.50 + 0.55 * vnoise(p * 5.0 + vec2(t, -t * 0.7));
    col += mix(uCream, uAmber, 0.55) * rim * rimBreak * mix(0.08, 0.20, foc);

    // Halo doux / bokeh autour des blobs flous.
    float glow = smoothstep(thr - soft - 0.10, thr, T) * (1.0 - mask) * (1.0 - foc);
    col += mix(uCream, uAmber, 0.4) * glow * 0.06;

    // SPECULAR : catchlight net sur les gouttes au point (le signe de l'huile vraie).
    float spec = smoothstep(mix(0.20, 0.10, foc), 0.0, length(toC - vec2(0.16, -0.18)));
    col += uCream * spec * mask * mix(0.25, 0.85, foc);
    // Micro-éclat secondaire (réflexion d'un autre point lumineux).
    float spec2 = smoothstep(0.06, 0.0, length(toC - vec2(-0.18, 0.16))) * mask;
    col += uCream * spec2 * 0.35;

    // Profondeur : la matière floue recule (plus subtile sur fond clair).
    col *= mix(0.86, 1.0, mix(1.0, foc, mask));

    // Léger éclairage vertical (lumière du haut).
    col *= 0.96 + 0.07 * uv.y;

    // Vignette douce + grain (anti-banding).
    float vig = smoothstep(1.20, 0.30, length(uv - 0.5));
    col *= 0.92 + 0.08 * vig;
    col += (hash1(uv * uRes.xy + uTime) - 0.5) * 0.018;

    gl_FragColor = vec4(col, 1.0);
  }
`;

function hexToRgb(hex, fallback) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec((hex || '').trim());
  if (!m) return fallback;
  return [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255];
}

function compileShader(gl, type, src) {
  const s = gl.createShader(type);
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(s) || 'shader compile error';
    gl.deleteShader(s);
    throw new Error(log);
  }
  return s;
}

class PoaimeHero3D {
  constructor(root) {
    this.root = root;
    this.canvas = root.querySelector('.poaime-hero-3d__canvas');
    this.ds = root.dataset;
    this.running = false;
    this.started = false;
    this._raf = null;

    const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const small = Math.min(window.innerWidth, window.innerHeight) <= 820;
    // Shader assez lourd : on plafonne la résolution interne (plus bas sur mobile).
    this.maxDpr = (coarse || small) ? 1.0 : 1.5;
    this.quality = (coarse || small) ? 0.7 : 0.9;
  }

  start() {
    if (this.started) return;
    this.started = true;
    try {
      this.build();
      this.bind();
      this.running = true;
      this.loop();
    } catch (e) {
      this.fail();
    }
  }

  fail() {
    // Le fallback CSS (dégradé) reste visible : on masque juste le canvas.
    this.root.classList.add('poaime-hero-3d--fallback');
    this.running = false;
  }

  build() {
    const gl =
      this.canvas.getContext('webgl', {
        antialias: false, alpha: false, depth: false, stencil: false,
        premultipliedAlpha: false, powerPreference: 'high-performance',
      }) || this.canvas.getContext('experimental-webgl');
    if (!gl) throw new Error('WebGL indisponible');
    this.gl = gl;

    const prog = gl.createProgram();
    gl.attachShader(prog, compileShader(gl, gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compileShader(gl, gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      throw new Error(gl.getProgramInfoLog(prog) || 'link error');
    }
    gl.useProgram(prog);
    this.prog = prog;

    // Triangle plein écran (couvre tout le viewport, zéro géométrie superflue).
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    this.u = {};
    ['uTime', 'uRes', 'uSpeed', 'uScale', 'uDeep', 'uMid', 'uAmber', 'uCream']
      .forEach((n) => { this.u[n] = gl.getUniformLocation(prog, n); });

    const cA = hexToRgb(this.ds.colorA, [0.14, 0.23, 0.18]);
    const cB = hexToRgb(this.ds.colorB, [0.25, 0.35, 0.29]);
    const cC = hexToRgb(this.ds.accent, [0.82, 0.59, 0.48]);
    const cr = hexToRgb(this.ds.cream, [0.96, 0.95, 0.92]);
    gl.uniform3fv(this.u.uDeep, cA);
    gl.uniform3fv(this.u.uMid, cB);
    gl.uniform3fv(this.u.uAmber, cC);
    gl.uniform3fv(this.u.uCream, cr);
    gl.uniform1f(this.u.uSpeed, parseFloat(this.ds.speed) || 1);
    // « density » repensé : densité des gouttes (cellules Voronoï sur la hauteur).
    gl.uniform1f(this.u.uScale, parseFloat(this.ds.density) || 3);

    this.t0 = performance.now();
    this.resize();
  }

  bind() {
    this._onResize = () => this.resize();
    window.addEventListener('resize', this._onResize, { passive: true });
    this.canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); this.pause(); });
  }

  resize() {
    const gl = this.gl;
    if (!gl) return;
    const w = this.root.clientWidth || window.innerWidth;
    const h = this.root.clientHeight || 600;
    const pr = Math.min(window.devicePixelRatio || 1, this.maxDpr) * this.quality;
    const bw = Math.max(1, Math.round(w * pr));
    const bh = Math.max(1, Math.round(h * pr));
    if (this.canvas.width !== bw || this.canvas.height !== bh) {
      this.canvas.width = bw;
      this.canvas.height = bh;
    }
    gl.viewport(0, 0, bw, bh);
    gl.uniform2f(this.u.uRes, bw, bh);
  }

  loop() {
    if (!this.gl || !this.running) { this._raf = null; return; }
    const t = (performance.now() - this.t0) / 1000;
    this.gl.uniform1f(this.u.uTime, t);
    this.gl.drawArrays(this.gl.TRIANGLES, 0, 3);
    this._raf = requestAnimationFrame(() => this.loop());
  }

  pause() {
    this.running = false;
    if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
  }

  resume() {
    if (this.gl && !this.running) {
      this.running = true;
      if (!this._raf) this.loop();
    }
  }
}

function boot() {
  if (window.__poaimeHero3DBooted) return;
  window.__poaimeHero3DBooted = true;
  const roots = document.querySelectorAll('[data-poaime-hero-3d]');
  if (!roots.length) return;
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  roots.forEach((root) => {
    if (reduce) { root.classList.add('poaime-hero-3d--fallback'); return; }

    const inst = new PoaimeHero3D(root);
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) { inst.start(); inst.resume(); }
          else { inst.pause(); }
        });
      }, { threshold: 0.05 });
      io.observe(root);
    } else {
      inst.start();
    }
  });
}

if (document.readyState !== 'loading') boot();
else document.addEventListener('DOMContentLoaded', boot);
